import { FC, useState } from "react";
import { AsyncSelect, FormField, TableCell, TableHeadCell } from "@rothoblaas/design-system";
import AdminCrudTable from "../../../../components/ui/AdminCrudTable";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ScrewDiameter } from "../../../../http/screwConnector/types/screwDiameters";
import { Screw, UpdateScrewBody } from "../../../../http/screwConnector/types/screws";
import useScrewConnectorScrewDiametersApi from "../../../../http/screwConnector/useAdminScrewDiameters";
import useScrewConnectorScrewsApi from "../../../../http/screwConnector/useAdminScrews";
import ScrewForm from "./ScrewForm";
import styles from "./AdminScrews.module.css";

// The inline visibility and order editors re-send the full entity (no /visibility nor /order endpoints).
const toUpdateBody = (item: Screw): UpdateScrewBody => ({
  itemId: item.item.id,
  screwDiameterId: item.screwDiameterId,
  length: item.length,
  partialThreadLength: item.partialThreadLength,
  fullThreadExtraLength: item.fullThreadExtraLength,
  tipLength: item.tipLength,
  visibility: item.visibility,
  order: item.order,
});

const AdminScrews: FC = () => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule;
  const screwsApi = useScrewConnectorScrewsApi();
  const screwDiametersApi = useScrewConnectorScrewDiametersApi();
  const screwDiameterOptions = useAsyncOptions(screwDiametersApi.getScrewDiameters);
  const [selectedDiameter, setSelectedDiameter] = useState<ScrewDiameter>();

  return (
    // the diameter filter above the shared table, which has the search only
    <div className={styles.root}>
      <div className={styles.filter}>
        <FormField label={t.screws.filterByDiameter} isOptional>
          {({ labelId }) => (
            <AsyncSelect<ScrewDiameter, false>
              {...screwDiameterOptions}
              inputId={labelId}
              value={selectedDiameter ?? null}
              isClearable
              isMulti={false}
              placeholder={`${i18n.translation.common.select}...`}
              getOptionLabel={(option) => `⌀${option.diameter} ${i18n.translation.common.unitMm}`}
              getOptionValue={(option) => option.id}
              onChange={(option) => setSelectedDiameter(option ?? undefined)}
            />
          )}
        </FormField>
      </div>
      <AdminCrudTable<Screw>
        filterPrefix="screws"
        useList={(query, signal) => screwsApi.useScrews(query, signal, selectedDiameter?.id)}
        head={
          <>
            <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.description}</TableHeadCell>
            <TableHeadCell align="left">{t.screws.fields.length}</TableHeadCell>
          </>
        }
        renderRow={(item) => (
          <>
            <TableCell>{item.item.code}</TableCell>
            <TableCell>{item.item.description}</TableCell>
            <TableCell>
              {item.length} {i18n.translation.common.unitMm}
            </TableCell>
          </>
        )}
        visibilityAndOrder={{
          updateVisibility: (item, visibility) =>
            screwsApi.updateScrew(item.item.id, { ...toUpdateBody(item), visibility }),
          updateOrder: (item, order) => screwsApi.updateScrew(item.item.id, { ...toUpdateBody(item), order }),
        }}
        onDelete={(item) => screwsApi.deleteScrew(item.item.id)}
        deleteTitle={t.screws.singular}
        canAdd
        // a new screw starts from the diameter the list is filtered by
        renderForm={(item, onClose) => (
          <ScrewForm screw={item} defaultScrewDiameter={item ? undefined : selectedDiameter} onClose={onClose} />
        )}
      />
    </div>
  );
};

export default AdminScrews;
